/**
 * Prompt Generator (spec section 14).
 *
 * Builds a provider-ready prompt from scene metadata + the user's
 * choices. The output is deliberately structured: automated providers
 * follow sections better than they follow a wall of text, and a human
 * pasting into Google Vids can still read it.
 */

import { MOODS, VIDEO_STYLES } from '../core/config.js';

/** How a mood should read in the prompt. */
const MOOD_DIRECTION = {
  TECH: { lighting: 'cool screen light mixed with warm practicals', palette: 'graphite, cool white, one warm accent', pace: 'steady and precise' },
  DARK: { lighting: 'low key, deep shadow, minimal fill', palette: 'near-black, desaturated blue', pace: 'slow and heavy' },
  SERIOUS: { lighting: 'restrained, motivated practicals only', palette: 'neutral grey with muted colour', pace: 'even and deliberate' },
  CALM: { lighting: 'soft diffused light, gentle falloff', palette: 'muted warm neutrals', pace: 'slow and quiet' },
  URBAN: { lighting: 'mixed street lighting, sodium and LED', palette: 'amber, teal, asphalt grey', pace: 'continuous and rolling' },
  FUTURISTIC: { lighting: 'clean cool emissive light, subtle bloom', palette: 'deep blue, cyan, white', pace: 'smooth and constant' },
  NEWS: { lighting: 'flat neutral illumination, no drama', palette: 'clean whites and cool greys', pace: 'calm and factual' },
  NIGHT: { lighting: 'night exterior with practical light sources', palette: 'deep blue, warm highlights', pace: 'slow and atmospheric' },
  ENERGETIC: { lighting: 'bright highlights, higher contrast', palette: 'saturated accents on dark base', pace: 'brisk but continuous' },
  NOSTALGIC: { lighting: 'soft warm light, slight haze', palette: 'faded amber and dusty green', pace: 'unhurried' },
};

const CAMERA_DIRECTION = {
  static: 'camera locked off on a tripod, no camera movement at all',
  'slow-pan': 'camera pans very slowly at constant speed, no stops and no reversals',
  'handheld-subtle': 'stable handheld with only micro movement, no shake and no drift',
  dashboard: 'camera fixed to the vehicle interior, movement comes only from the vehicle suspension',
  'window-fixed': 'camera fixed to the window frame, movement comes only from the vehicle',
};

const LOOP_DIRECTION = {
  normal: 'The clip will be repeated back to back, so the first frame and the last frame must match closely in composition, brightness and colour.',
  crossfade: 'The clip will be crossfaded into itself, so the start and the end must be visually and tonally compatible.',
  pingpong: 'The clip will be played forward and then reversed, so movement must be gentle and non-directional enough to read naturally in reverse.',
};

function moodLabel(moodId) {
  return MOODS.find((m) => m.id === moodId)?.label ?? moodId ?? 'neutral';
}

function styleLabel(styleId) {
  return VIDEO_STYLES.find((s) => s.id === styleId)?.label ?? styleId ?? 'Cinematic Realistic';
}

export function resolveMoodDirection(moodId) {
  return MOOD_DIRECTION[moodId] ?? MOOD_DIRECTION.SERIOUS;
}

export function resolveCameraDirection(cameraStyle) {
  return CAMERA_DIRECTION[cameraStyle] ?? CAMERA_DIRECTION.static;
}

/**
 * Build the prompt body (everything except the technical footer).
 * Exported separately so the UI can show a compact preview.
 */
export function buildPromptBody({
  scene,
  promptEntry = null,
  mood = null,
  style = null,
  loopMode = 'crossfade',
}) {
  if (!scene) throw new Error('buildPromptBody requires a scene');
  const moodId = mood ?? scene.moods?.[0] ?? 'SERIOUS';
  const direction = resolveMoodDirection(moodId);
  const styleId = style ?? promptEntry?.style ?? scene.visualStyle ?? 'cinematic-realistic';
  const core = (promptEntry?.prompt ?? scene.description ?? '').trim();

  const lines = [];
  lines.push(`SCENE: ${scene.name}`);
  lines.push(core);
  lines.push('');
  lines.push('SHOT');
  lines.push(`- Framing: vertical 9:16, subject centred, headroom reserved at the top of frame`);
  lines.push(`- Camera: ${resolveCameraDirection(scene.cameraStyle)}`);
  lines.push(`- Movement: continuous single take, no cuts, no transitions, no whip pans`);
  lines.push(`- Lighting: ${direction.lighting}`);
  lines.push(`- Colour: ${direction.palette}`);
  lines.push(`- Pace: ${direction.pace}`);
  lines.push(`- Look: ${styleLabel(styleId)}`);
  lines.push(`- Environment: ${(scene.environments ?? []).join(', ') || 'interior'}`);
  lines.push(`- Mood: ${moodLabel(moodId)}`);
  lines.push('');
  lines.push('LOOP REQUIREMENTS');
  lines.push(`- ${LOOP_DIRECTION[loopMode] ?? LOOP_DIRECTION.crossfade}`);
  lines.push('- Motion must be continuous and ambient: light, weather, traffic, dust, reflections, steam.');
  lines.push('- Keep the lighting level constant. No large changes in exposure or colour temperature.');
  lines.push('- No objects may enter or leave the frame. No people in focus.');
  lines.push('- No on-screen text, no subtitles, no logos, no watermarks, no UI elements.');
  lines.push('');
  lines.push('AUDIO');
  lines.push('- No dialogue, no narration, no lip movement. Ambient room tone only.');

  if (promptEntry?.negativePrompt) {
    lines.push('');
    lines.push('DO NOT INCLUDE');
    lines.push(`- ${promptEntry.negativePrompt}`);
  }

  return lines.join('\n');
}

/** Technical footer appended for the target provider. */
export function buildTechnicalFooter({ durationSeconds, aspectRatio, resolution, fps = 24, providerId = 'generic' }) {
  const lines = ['', 'TECHNICAL'];
  lines.push(`- Duration: ${durationSeconds} seconds, one continuous shot`);
  lines.push(`- Aspect ratio: ${aspectRatio}${aspectRatio === '9:16' ? ' (vertical, social media)' : ''}`);
  if (resolution) lines.push(`- Resolution: ${resolution}`);
  lines.push(`- Frame rate: ${fps} fps`);
  if (providerId === 'google-vids') {
    lines.push('- Generate a single clip. Do not add music, voice-over, captions or stock footage.');
    lines.push('- Export the clip as-is at the highest available quality.');
  }
  return lines.join('\n');
}

/**
 * Full provider-ready prompt.
 *
 * @returns {string} plain text, safe to copy to the clipboard or POST to the Worker
 */
export function generatePrompt({
  scene,
  promptEntry = null,
  mood = null,
  style = null,
  durationSeconds = 8,
  aspectRatio = '9:16',
  resolution = null,
  loopMode = 'crossfade',
  providerId = 'generic',
  fps = 24,
}) {
  const body = buildPromptBody({ scene, promptEntry, mood, style, loopMode });
  const footer = buildTechnicalFooter({ durationSeconds, aspectRatio, resolution, fps, providerId });
  return `${body}${footer}`;
}

/**
 * Google Vids flavour: same content, wrapped with an explicit
 * "manual step" instruction block, because a human is driving the tool.
 */
export function generateVidsPrompt(options) {
  const base = generatePrompt({ ...options, providerId: 'google-vids' });
  return [
    'BEX LOOP / BEX CAST - manual generation brief',
    '',
    'Generate ONE continuous vertical clip with no cuts. Use it as a repeating background loop for a technology audio episode.',
    '',
    base,
  ].join('\n');
}

/** Compact one-line summary used in library cards and history rows. */
export function summarizePrompt(text, max = 110) {
  const flat = String(text ?? '')
    .split('\n')
    .map((line) => line.replace(/^[-*\s]+/, '').trim())
    .filter((line) => line && !line.startsWith('SHOT') && !line.startsWith('TECHNICAL') && !line.startsWith('LOOP'))
    .join(' ');
  return flat.length > max ? `${flat.slice(0, max - 1)}\u2026` : flat;
}
